import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const SB = { active:'badge-green', completed:'badge-blue', on_hold:'badge-amber', cancelled:'badge-red' };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm] = useState({ name:'', description:'', status:'active', client_id:'', start_date:'', end_date:'' });

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/projects'), api.get('/clients')])
      .then(([p,c]) => { setProjects(p.data.projects); setClients(c.data.clients); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm({ name:'', description:'', status:'active', client_id:'', start_date:'', end_date:'' }); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name:p.name, description:p.description||'', status:p.status, client_id:p.client_id||'', start_date:p.start_date||'', end_date:p.end_date||'' }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await api.put(`/projects/${editing.id}`, form); toast.success('Project updated.'); }
      else         { await api.post('/projects', form);              toast.success('Project created.'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await api.delete(`/projects/${p.id}`); toast.success('Deleted.'); load(); }
    catch { toast.error('Failed.'); }
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Projects</h1><p className="page-sub">{projects.filter(p=>p.status==='active').length} active</p></div>
        <button className="btn btn-primary" onClick={openAdd}>+ New Project</button>
      </div>

      {loading ? <div className="loading">Loading…</div> : projects.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">📁</div><p>No projects yet.</p></div></div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
          {projects.map(p => (
            <div key={p.id} className="card" style={{display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:14,marginBottom:5}}>{p.name}</div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    <span className={`badge ${SB[p.status]||'badge-gray'}`}>{p.status.replace('_',' ')}</span>
                    {p.client && <span className="badge badge-purple">🏢 {p.client.name}</span>}
                  </div>
                </div>
                <div style={{display:'flex',gap:5,marginLeft:8}}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)}>Del</button>
                </div>
              </div>
              {p.description && <p style={{fontSize:12.5,color:'#6b7280',lineHeight:1.5}}>{p.description}</p>}
              <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
                {p.start_date && <span className="text-sm text-muted">▶ {format(new Date(p.start_date),'MMM d, yyyy')}</span>}
                {p.end_date   && <span className="text-sm text-muted">⏹ {format(new Date(p.end_date),'MMM d, yyyy')}</span>}
              </div>
              {p.employees?.length > 0 && (
                <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                  {p.employees.map(emp => (
                    <div key={emp.id} style={{display:'flex',alignItems:'center',gap:5,background:'#22263a',borderRadius:20,padding:'3px 8px 3px 5px'}}>
                      <div className="avatar" style={{width:20,height:20,fontSize:9}}>{emp.name.charAt(0)}</div>
                      <span style={{fontSize:11.5,color:'#d1d5db'}}>{emp.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-title">{editing ? 'Edit Project' : 'New Project'}</div>
            <form onSubmit={handleSave} className="form-grid">
              <div className="field"><label>Project Name *</label><input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Customer Portal v2"/></div>
              <div className="field"><label>Description</label><textarea rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Brief description…"/></div>
              <div className="form-row">
                <div className="field"><label>Client</label>
                  <select value={form.client_id} onChange={e=>setForm({...form,client_id:e.target.value})}>
                    <option value="">Select client…</option>
                    {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="field"><label>Status</label>
                  <select value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                    {['active','completed','on_hold','cancelled'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="field"><label>Start Date</label><input type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})}/></div>
                <div className="field"><label>End Date</label><input type="date" value={form.end_date} onChange={e=>setForm({...form,end_date:e.target.value})}/></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving…':editing?'Save Changes':'Create Project'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
